import { orderDel, orderFinish, orderList } from "@/apis/inspection";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { CommonListParams } from "@/types/common";
import { InspectionListParams, InspectionOrder, InspectionOrderStatus } from "@/types/inspection";
import { Button, DatePicker, Flex, Input, notification, Select, TableProps, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import dayjs, { Dayjs } from "dayjs";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useInspactionEnum from "../hooks/useInspactionEnum";
import SText from "@/styles/SText";
import { dateFormat } from "@/utils/format";
import InspectionDetail from "./InspectionDetail";
import { SConModal } from "@/styles/SConModal";
import AddInspection from "./AddInspection";
const { RangePicker } = DatePicker;

const InspectionList: FC = () => {
  const { t } = useTranslation();
  const { InspectionStatusEnums, getInspectionStatus } = useInspactionEnum();
  const [list, total, loading, { fetchList, updateItem, addItem }] = useDataList<InspectionOrder, [params: CommonListParams<InspectionListParams>]>({ fetchApi: orderList })
  const abortCtl = useRef<AbortController | null>(null);
  const tableRef = useRef<TableLayoutRef>(null);

  const [keyword, setKeyword] = useState<string>();
  const [startTime, setStartTime] = useState<number>();
  const [endTime, setEndTime] = useState<number>();
  const [status, setStatus] = useState<InspectionOrderStatus>();
  const [detail, setDetail] = useState<InspectionOrder | null>(null);
  const [cancelRow, setCancelRow] = useState<InspectionOrder | null>(null);
  const [openAdd, setOpenAdd] = useState<boolean>(false);

  useEffect(() => {
    fetch(0);
    return () => {
      abortCtl.current?.abort();
    }
  }, []);

  const fetch = (offset: number) => {
    if (offset === 0) tableRef.current?.resetTablePage();
    abortCtl.current?.abort();
    const controller = new AbortController();
    abortCtl.current = controller;
    fetchList(
      {
        body: { keyword, status, startTime, endTime },
        pagination: { take: tableRef.current?.pageSize || DEFAULT_PAGE_SIZE, offset },
        controller
      }
    )
  };

  const handleCancel = async (row: InspectionOrder, type: "C" | "F") => {
    try {
      setCancelRow(row);
      if (type === "C") {
        await orderDel(row.id);
        notification.success({ message: t('inspection.rejected'), description: row.number });
        updateItem({
          ...row,
          status: InspectionOrderStatus.REJECTED
        });
      }
      if (type === "F") {
        await orderFinish(row.id);
        notification.success({ message: t('inspection.finishOk'), description: row.number });
        updateItem({
          ...row,
          status: InspectionOrderStatus.RESOLVED
        });
      }
    } catch (error: any) {
      let desc = '';
      if (error === 74310) desc = t('inspection.unFinish')
      if (type === "C") {
        notification.error({ message: t('inspection.cancelErr') });
      }
      if (type === "F") {
        notification.error({ message: t('inspection.finishErr'), description: desc });
      }
    } finally { setCancelRow(null) };
  };

  const columns: TableProps<InspectionOrder>['columns'] = [
    {
      title: t("inspection.number"),
      key: "number",
      width: 140,
      fixed: "left",
      align: "center",
      render: (_, record) => (
        <Typography.Text
          ellipsis={{ tooltip: record?.number ?? "" }}
          style={{ color: '#1890ff', width: '100%', cursor: "pointer" }}
          onClick={() => setDetail(record)}
        >
          {record?.number ?? ""}
        </Typography.Text>
      )
    },
    {
      title: t("inspection.state"),
      key: "state",
      width: 160,
      align: "center",
      render: (_, record) => {
        let info = getInspectionStatus(record.status);
        return info && <SText tagMode text={info.label} color={info.color} />
      }
    },
    {
      title: t("inspection.createdAt"),
      key: "createdAt",
      width: 100,
      align: "center",
      render: (_, record) => record.createdAt && <SText text={dateFormat(dayjs(record.createdAt))} />
    },
    {
      title: t("inspection.updatedAt"),
      key: "updatedAt",
      width: 100,
      align: "center",
      render: (_, record) => record.updatedAt && <SText text={dateFormat(dayjs(record.updatedAt))} />
    },
    {
      title: t("inspection.planAt"),
      key: "planAt",
      width: 100,
      align: "center",
      render: (_, record) => record.planAt && <SText text={dateFormat(dayjs(record.planAt))} />
    },
    {
      title: t("inspection.finishedAt"),
      key: "finishedAt",
      width: 100,
      align: "center",
      render: (_, record) => record.finishedAt && <SText text={dateFormat(dayjs(record.finishedAt))} />
    },
    {
      title: t("inspection.remark"),
      key: "remark",
      width: 120,
      align: "center",
      render: (_, record) => record.remark && <SText text={record.remark || ''} />
    },
    {
      title: t("common.opt"),
      key: "opt",
      width: 100,
      align: "center",
      render: (_, record) => {
        // let disable = !!cancelRow || [InspectionOrderStatus.REJECTED, InspectionOrderStatus.RESOLVED].includes(record.status);
        return (
          <Fragment>
            <Button type="link" onClick={() => setDetail(record)} >{t('inspection.detail')}</Button>
            {/* <SConModal title={t('common.tip')} content={t('inspection.cancelTip')} disable={disable} onOk={() => handleCancel(record, "C")} >
              <Button type="link" disabled={disable} danger>{t('inspection.cancel')}</Button>
            </SConModal>
            <SConModal title={t('common.tip')} content={t('inspection.finishTip')} disable={disable} onOk={() => handleCancel(record, "F")} >
              <Button type="primary" disabled={disable} >{t('inspection.finish')}</Button>
            </SConModal> */}
          </Fragment>
        )
      }
    }
  ];

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between" align="center">
      <Flex gap={6}>
        <Button type="primary" onClick={() => setOpenAdd(true)}>{t('inspection.add')}</Button>
      </Flex>
      <Flex gap={4} align="center">
        <RangePicker
          value={[startTime ? dayjs(startTime) : null, endTime ? dayjs(endTime) : null]}
          onChange={val => {
            if (val) {
              setStartTime(val[0]?.startOf('day').valueOf())
              setEndTime(val[1]?.endOf('day').valueOf())
            } else {
              setStartTime(undefined)
              setEndTime(undefined)
            }
          }} allowClear
          placeholder={[t('order.startTime'), t('order.endTime')]}
          style={{ width: 280 }}
        />
        <Select options={InspectionStatusEnums} onChange={setStatus} value={status}
          placeholder={t('inspection.plState')} style={{ width: 200 }} allowClear
        />
        <Input.Search value={keyword}
          onChange={e => setKeyword(e.target.value)}
          enterButton loading={loading} onSearch={() => fetch(0)}
          placeholder={t('category.plSearch')}
        />
      </Flex>
    </Flex>
  )

  return (
    <Fragment>
      <STableLayout
        dataSource={list}
        total={total}
        loading={loading}
        columns={columns as Array<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        extHeight={40}
        handleChange={fetch}
        scrollX={800}
      />
      {detail && <InspectionDetail open detail={detail} onClose={() => setDetail(null)} onUpdate={updateItem} />}
      {openAdd && <AddInspection open={openAdd} onClose={() => setOpenAdd(false)} onSuccess={addItem} />}
    </Fragment>
  )
};

export default InspectionList;